import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrayHelpers, DeleteWarning, Forms, Picker, SystemText } from '@salesperformanceint/e4shared';
import { addImage, setShowSuccess } from 'actions/ui';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { apiDelete, apiGet, apiPost, apiPatch } from 'services';
import { hasModule } from 'helpers/profile';
import RoleDescriptions from './components/roleDescriptions';
import SortCompetencies from './components/sortCompetencies';
import { adminLoading, filePromises, handleError } from 'actions/admin';
import config from '../../../config';

export const AdminCompetencies = ({ history }) => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState([]);
    const [areas, setAreas] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [jobs, setJobs] = React.useState([]);
    const [integrationFields, setIntegrationFields] = React.useState([]);
    const [realtimeFields, setRealtimeFields] = React.useState([]);
    const [showDescriptions, setShowDescriptions] = React.useState(false);
    const [hasMappedData, setHasMappedData] = React.useState(false);
    const [descriptionsCompetency, setDescriptionsCompetency] = React.useState(null);
    const [pickerItem, setPickerItem] = React.useState(null);
    const [showRolePicker, setShowRolePicker] = React.useState(false);
    const [showCRMPicker, setShowCRMPicker] = React.useState(false);
    const [showRealtimePicker, setShowRealtimePicker] = React.useState(false);
    const [showMetricsPicker, setShowMetricsPicker] = React.useState(false);
    const [showSort, setShowSort] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [deleting, setDeleting] = React.useState(null);
    const [migrate, setMigrate] = React.useState(null);

    const user = useSelector(state => state.user.user);
    const usePIs = user.companySettings?.development?.usePositiveIndicators;
    const prismUI = user.companySettings?.prism_UI;

    const text = {
        label: SystemText('Admin/Competency/Label'),
        labelPlural: SystemText('Admin/Competency/Label/Plural'),
        bulkWarning: SystemText('Admin/Competency/Bulk/Warning'),
        filterOn: SystemText('Admin/Competency/FilterOn'),
        filterHelp: SystemText('Admin/Competency/FilterHelp'),
        success: SystemText('Admin/Competency/Success'),
        orderSuccess: SystemText('Admin/Competency/OrderSuccess'),
        table: {
            columns: {
                competency: SystemText('Admin/Competency/Table/Columns/Competency'),
                area: SystemText('Admin/Competency/Table/Columns/Area'),
                comments: SystemText('Admin/Competency/Table/Columns/Comments'),
                roles: SystemText('Admin/Competency/Table/Columns/Roles'),
                positiveIndicators: SystemText('Admin/Competency/Table/Columns/Positive'),
                crm: SystemText('Admin/Competency/Table/Columns/CRM'),
                realtime: SystemText('Admin/Competency/Table/Columns/Real'),
                other: SystemText('Admin/Competency/Table/Columns/Other'),
            },
            buttons: {
                override: x => SystemText(`Admin/Competency/Table/Buttons/Override${usePIs ? '/PIs' : ''}`, x),
                activate: x => SystemText('Admin/Competency/Table/Buttons/Deactivate', x),
                deactivate: x => SystemText('Admin/Competency/Table/Buttons/Activate', x),
                delete: x => SystemText('Admin/Competency/Table/Buttons/Delete', x),
                deleteDisabled: SystemText('Admin/Competency/Table/Buttons/Delete/Disabled'),
            },
        },
        fields: {
            competency: {
                label: SystemText('Admin/Competency/Fields/Competency'),
            },
            sysName: {
                label: SystemText('Admin/Competency/Fields/SysName'),
                help: SystemText('Admin/Competency/Fields/SysName/Help'),
            },
            area: {
                label: SystemText('Admin/Competency/Fields/Area'),
                help: SystemText('Admin/Competency/Fields/Area/Help'),
                placeholder: SystemText('Admin/Competency/Fields/Area/Placeholder'),
            },
            tags: {
                label: SystemText('Admin/Competency/Fields/Tags'),
                help: SystemText('Admin/Competency/Fields/Tags/Help'),
            },
            image: {
                label: SystemText('Admin/Competency/Fields/Image'),
                help: SystemText('Admin/Competency/Fields/Image/Help'),
                placeholder: SystemText('Admin/Competency/Fields/Image/Placeholder'),
            },
            roles: {
                label: SystemText('Admin/Competency/Fields/Roles'),
                help: SystemText('Admin/Competency/Fields/Roles/Help'),
                placeholder: SystemText('Admin/Competency/Fields/Roles/Placeholder'),
            },
            description: {
                label: SystemText('Admin/Competency/Fields/Description'),
                help: SystemText('Admin/Competency/Fields/Description/Help'),
            },
            crm: {
                label: SystemText('Admin/Competency/Fields/CRM'),
                help: SystemText('Admin/Competency/Fields/CRM/Help'),
                placeholder: SystemText('Admin/Competency/Fields/CRM/Placeholder'),
            },
            staff: {
                label: SystemText('Admin/Competency/Fields/Staff'),
                help: SystemText('Admin/Competency/Fields/Staff/Help'),
                placeholder: SystemText('Admin/Competency/Fields/Staff/Placeholder'),
            },
            realtime: {
                label: SystemText('Admin/Competency/Fields/Real'),
                help: SystemText('Admin/Competency/Fields/Real/Help'),
                placeholder: SystemText('Admin/Competency/Fields/Real/Placeholder'),
            },
            integration: {
                label: x => SystemText('Admin/Competency/Fields/Integration', x),
                help: x => SystemText('Admin/Competency/Fields/Integration/Help', x),
                placeholder: SystemText('Admin/Competency/Fields/Integration/Placeholder'),
            },
            review: {
                label: SystemText('Admin/Competency/Fields/Review'),
                help: SystemText('Admin/Competency/Fields/Review/Help'),
            },
            mandatory: {
                label: SystemText('Admin/Competency/Fields/Mandatory'),
                help: SystemText('Admin/Competency/Fields/Mandatory/Help'),
            },
            active: {
                label: SystemText('Admin/Competency/Fields/Active'),
                help: SystemText('Admin/Competency/Fields/Active/Help'),
            },
        },
        pickers: {
            roles: {
                entity: SystemText('Admin/Competency/Pickers/Roles'),
                label: x => SystemText('Admin/Competency/Pickers/Roles/Label', x),
                success: SystemText('Admin/Competency/Pickers/Roles/Success'),
            },
            crm: {
                entity: SystemText('Admin/Competency/Pickers/CRM'),
                label: x => SystemText('Admin/Competency/Pickers/CRM/Label', x),
                success: SystemText('Admin/Competency/Pickers/CRM/Success'),
            },
            realtime: {
                entity: SystemText('Admin/Competency/Pickers/Real'),
                label: x => SystemText('Admin/Competency/Pickers/Real/Label', x),
                success: SystemText('Admin/Competency/Pickers/Real/Success'),
            },
            other: {
                entity: SystemText('Admin/Competency/Pickers/Other'),
                label: x => SystemText('Admin/Competency/Pickers/Other/Label', x),
                success: SystemText('Admin/Competency/Pickers/Other/Success'),
            },
        },
        descriptionSuccess: SystemText('Admin/Competency/DescSuccess'),
        delete: {
            warning: SystemText('Admin/Competency/Delete/Warning'),
            title: SystemText('Admin/Competency/Delete/Title'),
            label: SystemText('Admin/Competency/Delete/Label'),
        },
    };

    React.useEffect(() => {
        dispatch(adminLoading(true));
        api.load();
        apiGet('competency-area', user.guid, response => setAreas(response.sort((a, b) => (a.name > b.name ? 1 : -1))));
        apiGet('role', user.guid, response => setRoles(response));
        apiGet('crm-job', user.guid, response => setJobs(response));
        apiGet('integration-fields', user.guid, response => {
            setIntegrationFields(
                response.map(f => ({ ...f, extra: undefined })).filter(f => user.integrations.some(i => i.ref === f.ref))
            );
        });
        apiGet('crm-realtime', user.guid, response => setRealtimeFields(response));
    }, []);

    const api = {
        load: () => {
            dispatch(adminLoading(true));
            apiGet('competency', user.guid, response => {
                setData(response.map(c => ({ ...c, cannotDelete: !user.support && !!c.system_Name })));
                dispatch(adminLoading(false));
            });
        },
        add: (entity, callback) => {
            Promise.all(filePromises(entity, ['competency_Image'], user.guid, config)).then(() => {
                apiPost(
                    'competency',
                    entity,
                    user.guid,
                    () => {
                        dispatch(setShowSuccess(text.success));
                        dispatch(addImage('competency_Image', entity.competency_Image, 0, true));
                        callback();
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            });
        },
        update: (entity, callback, bulk) => {
            Promise.all(filePromises(entity, ['competency_Image'], user.guid, config)).then(() => {
                apiPatch(
                    'competency',
                    entity,
                    user.guid,
                    () => {
                        !bulk && dispatch(setShowSuccess(text.success));
                        dispatch(addImage('competency_Image', entity.competency_Image, 0, true));
                        callback();
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            });
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(
                    `competency/${id}?migrate=${migrate}`,
                    null,
                    user.guid,
                    () => idx + 1 === ids.length && callback()
                );
            });
        },
    };

    const deleteItem = confirm => {
        setShowDelete(false);
        setDeleting(null);
        if (confirm) {
            api.remove(deleting, () => {
                dispatch(setShowSuccess(SystemText('Admin/General/Deleted', text.label.toLowerCase())));
                api.load();
            });
        }
    };

    if (!user.sys_Config) history.replace('/');

    const options = {
        ...DefaultOptions(),
        label: text.label,
        labelPlural: text.labelPlural,
        clickOnZero: true,
        showActive: true,
        showDelete: false,
        bulkDeleteWarning: text.bulkWarning,
        extraOptions: [
            {
                btnClass: 'btn-primary',
                disabled: d => !d.role_Ids || d.role_Ids.length === 0,
                label: d => text.table.buttons.override(d.name),
                icon: 'fa-pen-to-square',
                count: 'override_Role_Ids',
                onClick: d => {
                    setDescriptionsCompetency(d);
                    setShowDescriptions(true);
                },
            },
            {
                btnClass: 'btn-danger',
                icon: 'fa-trash',
                bulk: 'Delete',
                disabled: d => d.positiveIndicatorCount > 0 || d.cannotDelete,
                label: d =>
                    d.cannotDelete
                        ? SystemText('Admin/Table/Delete/System', text.label?.toLowerCase())
                        : d.positiveIndicatorCount > 0
                          ? text.table.buttons.deleteDisabled
                          : text.table.buttons.delete(d.name),
                onClick: d => {
                    setShowDelete(true);
                    setDeleting(d.id);
                },
                after: true,
            },
        ],
    };

    let fields = [
        {
            label: text.fields.sysName.label,
            type: 'text',
            readonly: !user.support,
            mandatory: true,
            dataProperty: 'system_Name',
            maxLength: 50,
            hideField: (values, entity) => !user.support && !values.system_Name,
            help: text.fields.sysName.help,
        },
        {
            label: text.fields.competency.label,
            type: 'text',
            dataProperty: 'name',
            mandatory: true,
            maxLength: 100,
            generalErrors: true,
            uniqueErrors: true,
        },
        {
            label: text.fields.area.label,
            type: 'select',
            dataProperty: 'area_Id',
            data: areas,
            mandatory: true,
            allowMultiple: false,
            placeholder: text.fields.area.placeholder,
            help: text.fields.area.help,
        },
        {
            label: text.fields.roles.label,
            type: 'select',
            dataProperty: 'role_Ids',
            data: roles,
            mandatory: false,
            allowMultiple: true,
            placeholder: text.fields.roles.placeholder,
            help: text.fields.roles.help,
        },
        {
            label: text.fields.image.label,
            type: 'image',
            folder: 'competency_Image',
            ratio: 4, // times wider than higher (supports 1, 1.5, 2.5, 4)
            image: true,
            dataProperty: 'competency_Image',
            placeholder: text.fields.image.placeholder,
            help: text.fields.image.help,
        },
        {
            label: text.fields.description.label,
            type: 'textarea',
            dataProperty: 'description',
            rows: 5,
            help: text.fields.description.help,
        },
        {
            label: text.fields.tags.label,
            type: 'tags',
            dataProperty: 'tags',
            readonlyTagsProperty: user.support ? undefined : 'core_Tags',
            help: text.fields.tags.help,
        },
        {
            type: 'hidden',
            dataProperty: 'core_Tags',
        },
    ];

    if (user.crmIntegration && hasModule(user, 'integrations')) {
        fields.push({
            label: text.fields.crm.label,
            type: 'select',
            help: text.fields.crm.help,
            dataProperty: 'crm_Job_Ids',
            data: jobs,
            allowMultiple: true,
            placeholder: text.fields.crm.placeholder,
        });

        fields.push({
            label: text.fields.realtime.label,
            type: 'select',
            help: text.fields.realtime.help,
            dataProperty: 'crm_Realtime_Ids',
            data: realtimeFields,
            allowMultiple: true,
            placeholder: text.fields.realtime.placeholder,
            inTitleBar: true,
        });
    }

    if (!prismUI) {
        fields = fields.filter(f => f.dataProperty !== 'competency_Image');
    }

    React.useEffect(() => {
        if (data.length > 0 && integrationFields.length > 0 && !hasMappedData) {
            setHasMappedData(true);
            setData(
                data.map(c => {
                    return {
                        ...c,
                        integration_Field_Ids: (c.integration_Field_Ids || []).filter(id =>
                            integrationFields.some(f => f.id === id)
                        ),
                    };
                })
            );
        }
    }, [data, integrationFields]);

    const label = ref => text.fields.integration.label(integrationFields.find(f => f.ref === ref)?.integration);

    user.integrations &&
        hasModule(user, 'integrations') &&
        user.integrations
            .sort((a, b) => (label(a.ref) > label(b.ref) ? 1 : -1))
            .forEach(i => {
                const filteredFields = integrationFields.filter(f => f.ref === i.ref);
                if (filteredFields.length > 0) {
                    fields.push({
                        label: text.fields.integration.label(filteredFields[0].integration),
                        type: 'select',
                        help: text.fields.integration.help(filteredFields[0].integration),
                        dataProperty: 'integration_Field_Ids',
                        dataRef: i.ref,
                        data: filteredFields,
                        allowMultiple: true,
                        placeholder: text.fields.integration.placeholder,
                        inTitleBar: true,
                    });
                }
            });

    fields.push({
        label: text.fields.review.label,
        help: text.fields.review.help,
        type: 'toggle',
        dataProperty: 'review_Comment',
        inTitleBar: true,
    });

    fields.push({
        label: text.fields.mandatory.label,
        help: text.fields.mandatory.help,
        type: 'toggle',
        toggleProperty: 'review_Comment',
        dataProperty: 'review_Comment_Mandatory',
        inTitleBar: true,
    });

    fields.push({
        label: text.fields.active.label,
        help: text.fields.active.help,
        type: 'toggle',
        dataProperty: 'active',
        inTitleBar: true,
    });

    const tableColumns = [
        { label: text.table.columns.competency, dataProperty: 'name' },
        { label: text.table.columns.area, dataProperty: 'area' },
        { label: text.table.columns.comments, dataProperty: 'review_Comment', toggle: true },
        {
            label: text.table.columns.roles,
            dataProperty: 'role_Ids',
            searchProperty: 'role_Names',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowRolePicker(true);
            },
        },
    ];

    if (usePIs) {
        tableColumns.push({
            label: text.table.columns.positiveIndicators,
            dataProperty: 'positiveIndicatorCount',
            format: x => (!x ? '0' : x),
            onClick: item => {
                history.push(`/admin/positive-indicators?cid=${item.id}&cname=${item.name}`);
            },
        });
    }

    if (user.crmIntegration && hasModule(user, 'integrations')) {
        tableColumns.push({
            label: text.table.columns.crm,
            dataProperty: 'crm_Job_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowCRMPicker(true);
            },
        });

        tableColumns.push({
            label: text.table.columns.realtime,
            dataProperty: 'crm_Realtime_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowRealtimePicker(true);
            },
        });
    }

    if (integrationFields?.length > 0 && hasModule(user, 'integrations')) {
        tableColumns.push({
            label: text.table.columns.other,
            dataProperty: 'integration_Field_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowMetricsPicker(true);
            },
        });
    }

    const extraButtons = (
        <button
            className="btn btn-primary"
            onClick={() => {
                setShowSort(true);
            }}
        >
            <i className="fa fa-sort-amount-asc mr10" />
            {SystemText('Admin/Competency/Buttons/Sort')}
        </button>
    );

    const defaultValues = { active: true };

    return (
        <div>
            <SimpleAdmin
                data={data}
                load={api.load}
                add={api.add}
                update={api.update}
                remove={api.remove}
                defaultValues={defaultValues}
                fields={fields}
                options={options}
                columns={tableColumns}
                filterHelp={text.filterHelp}
                filterTextOn={text.filterOn}
                filterColumn="active"
                extraButtons={extraButtons}
            />
            <Picker
                title={text.pickers.roles.label(pickerItem?.name)}
                entity={text.pickers.roles.entity}
                data={roles}
                value={pickerItem?.role_Ids}
                show={showRolePicker}
                onSave={ids => {
                    api.update({ ...pickerItem, update_Role_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.roles.success));
                        setShowRolePicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowRolePicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="street-view"
            />
            <Picker
                title={text.pickers.crm.label(pickerItem?.name)}
                entity={text.pickers.crm.entity}
                data={jobs}
                value={pickerItem?.crm_Job_Ids}
                show={showCRMPicker}
                onSave={ids => {
                    api.update({ ...pickerItem, crm_Job_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.crm.success));
                        setShowCRMPicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowCRMPicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="line-chart"
            />
            <Picker
                title={text.pickers.realtime.label(pickerItem?.name)}
                entity={text.pickers.realtime.entity}
                data={realtimeFields}
                value={pickerItem?.crm_Realtime_Ids}
                show={showRealtimePicker}
                onSave={ids => {
                    api.update({ ...pickerItem, crm_Realtime_Ids: ids }, () => {
                        dispatch(setShowSuccess(text.pickers.realtime.success));
                        setShowRealtimePicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowRealtimePicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="line-chart"
            />
            {showMetricsPicker && (
                <Picker
                    title={text.pickers.other.label(pickerItem?.name)}
                    entity={text.pickers.other.entity}
                    data={integrationFields}
                    nameProperty="full_Name"
                    value={pickerItem?.integration_Field_Ids}
                    show={showMetricsPicker}
                    onSave={ids => {
                        api.update({ ...pickerItem, integration_Field_Ids: ids }, () => {
                            dispatch(setShowSuccess(text.pickers.other.success));
                            setShowMetricsPicker(false);
                            api.load();
                        });
                    }}
                    onClose={() => {
                        setPickerItem(null);
                        setShowMetricsPicker(false);
                        dispatch(setShowSuccess(false));
                    }}
                    itemPrefix="line-chart"
                />
            )}
            <RoleDescriptions
                show={showDescriptions}
                roles={roles}
                competency={descriptionsCompetency}
                close={() => setShowDescriptions(false)}
                reload={() => {
                    dispatch(setShowSuccess(text.descriptionSuccess));
                    api.load();
                }}
            />
            <DeleteWarning
                message={text.delete.title}
                show={showDelete}
                close={confirm => deleteItem(confirm)}
                extra={() => (
                    <div>
                        <div className="mt20 mb20 text-sm">
                            <p className="copy">{text.delete.warning}</p>
                        </div>
                        <Forms.Select
                            placeholder={text.delete.label}
                            fa
                            fa-warning
                            fa-lg
                            text-danger
                            data={data
                                .filter(d => d.id !== deleting)
                                .sort((a, b) => (a.full_Name > b.full_Name ? 1 : -1))
                                .map(d => ({ ...d, name: d.full_Name }))}
                            value={migrate}
                            onChange={setMigrate}
                        />
                    </div>
                )}
            />
            {ArrayHelpers.existsAndHasLength(data) && setShowSort && (
                <SortCompetencies
                    show={showSort}
                    close={() => {
                        setShowSort(false);
                    }}
                    refresh={() => {
                        dispatch(setShowSuccess(text.orderSuccess));
                        api.load();
                    }}
                    competencies={[...data]}
                />
            )}
        </div>
    );
};

export default AdminCompetencies;
